import { idsOf, MigrationSpec, MongoFilter } from "../migration.models"

type SkillDefinition = Record<string, unknown> & {
  scope?: { channelIds?: string[]; messageTypes?: string[] }
}

const needsEmail = (definition: string): MongoFilter => ({
  $and: [
    { [`${definition}.scope.messageTypes`]: "TEXT_MESSAGE" },
    { [`${definition}.scope.messageTypes`]: { $ne: "EMAIL" } },
    {
      $or: [
        { [`${definition}.scope.channelIds`]: { $exists: false } },
        { [`${definition}.scope.channelIds`]: { $size: 0 } },
        {
          [`${definition}.scope.channelIds`]: {
            $in: idsOf(["EmailAccounts"])
          }
        }
      ]
    }
  ]
})

const previewDefinition = (definition: unknown): unknown => {
  const typed = definition as SkillDefinition | undefined
  const messageTypes = typed?.scope?.messageTypes ?? []
  if (
    !messageTypes.includes("TEXT_MESSAGE") ||
    messageTypes.includes("EMAIL")
  ) {
    return definition
  }
  const added = typed?.scope?.channelIds?.length
    ? "EMAIL (if one of these channels is an e-mail account)"
    : "EMAIL"
  return {
    ...typed,
    scope: { ...typed?.scope, messageTypes: [...messageTypes, added] }
  }
}

const aiEmailSkills: MigrationSpec = {
  type: "MIGRATE_AI_EMAIL_SKILLS",
  collection: "Skills",
  risk: "SAFE",
  title: {
    en: "Chat-scoped skills that reached e-mail → the EMAIL message type",
    tr: "E-postaya ulaşan chat kapsamlı Skill'ler → EMAIL mesaj tipi"
  },
  description: {
    en:
      "E-mail used to match skills scoped to TEXT_MESSAGE; it now has its own EMAIL bucket (e-mail " +
      "threads and e-mail tickets). Adds EMAIL to `scope.messageTypes` of every draft or " +
      "published definition scoped to TEXT_MESSAGE without EMAIL that reached e-mail — no " +
      "channel scope, or an e-mail account in `scope.channelIds` — so the skill keeps " +
      "triggering where it did. Only the matching definition changes; nothing is republished.",
    tr:
      "E-posta eskiden TEXT_MESSAGE kapsamlı Skill'lerle eşleşiyordu; artık kendi EMAIL kovası " +
      "var (e-posta thread'leri ve e-posta ticket'ları). TEXT_MESSAGE kapsamlı, EMAIL içermeyen " +
      "ve e-postaya ulaşan — kanal kapsamı yok ya da `scope.channelIds` içinde bir e-posta hesabı " +
      "var — her taslak veya yayınlanmış tanımın `scope.messageTypes` listesine EMAIL ekler; " +
      "böylece Skill önceden tetiklendiği yerde tetiklenmeye devam eder. Yalnızca eşleşen tanım " +
      "değişir; yeniden yayınlama olmaz."
  },
  affectedFilter: {
    $or: [needsEmail("draftDefinition"), needsEmail("publishedDefinition")]
  },
  sampleFields: [
    "customer",
    "name",
    "draftDefinition.scope.messageTypes",
    "draftDefinition.scope.channelIds",
    "publishedDefinition.scope.messageTypes",
    "publishedDefinition.scope.channelIds"
  ],
  preview: (doc) => ({
    ...doc,
    draftDefinition: previewDefinition(doc.draftDefinition),
    publishedDefinition: previewDefinition(doc.publishedDefinition)
  }),
  notes: [
    {
      en:
        "Skill channel ids are stored as strings, and `$idsOf` resolves to id strings, so the " +
        "e-mail account match is direct. `$idsOf` is resolved server-side against EmailAccounts.",
      tr:
        "Skill kanal kimlikleri string olarak saklanır ve `$idsOf` string kimliklere çözülür; " +
        "e-posta hesabı eşleşmesi doğrudandır. `$idsOf` sunucu tarafında EmailAccounts'a karşı çözülür."
    },
    {
      en:
        "Not touched: a definition with no message-type scope (it already means every type), and " +
        "one scoped only to non-e-mail channels (it never reached e-mail).",
      tr:
        "Dokunulmayanlar: mesaj tipi kapsamı olmayan tanım (zaten her tipi kapsar) ve yalnızca " +
        "e-posta dışı kanallara bağlı tanım (e-postaya hiç ulaşmadı)."
    },
    {
      en: "`draftRevision` is not touched, so an editor open on the skill keeps working.",
      tr: "`draftRevision` değişmez; Skill'i açık tutan bir editör çalışmaya devam eder."
    }
  ]
}

export default aiEmailSkills
